import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { FiMessageSquare, FiZap, FiAlertCircle, FiRefreshCw, FiSettings, FiFileText, FiEdit3 } from 'react-icons/fi';
import ChatMessage from '../chat/ChatMessage';
import ChatInput from '../chat/ChatInput';
import MacSubscribePrompt from '../amps/MacSubscribePrompt';
import { useAutoScroll } from '../../hooks/useAutoScroll';
import { sendMacAIMessage, createMacAIConversation, getMacAIConversations, getMacAIConversationMessages } from '../../services/macAIChatService';
import type { MacAIConversation } from '../../services/macAIChatService';
import { FusionNowNotSubscribedError } from '../../services/fusionNowAuth';
import type { ChatMessage as ChatMessageType } from '../chat/chat.types';
import '../../styles/AmpChat.css';
import '../../styles/ChannelChat.css';
import './MacAIChatPage.css';
const MAC_PROMPTS = [
    {
        id: 'p1',
        icon: <FiZap size={15}/>,
        color: '#00b0d3',
        label: 'Build a Renewal Follow-Up Workflow',
        prompt: 'Create a new renewal follow-up workflow'
    },
    {
        id: 'p2',
        icon: <FiRefreshCw size={15}/>,
        color: '#a200ff',
        label: 'List My Active Workflows',
        prompt: 'Show me all my active workflows'
    },
    {
        id: 'p3',
        icon: <FiFileText size={15}/>,
        color: '#4ed56e',
        label: 'Browse Pre Built Use Cases',
        prompt: 'Show me use case explorer'
    },
    {
        id: 'p4',
        icon: <FiSettings size={15}/>,
        color: '#ff6820',
        label: 'List All Workflow Templates',
        prompt: 'What workflow templates are available?'
    }
];
function makeId(): string {
    return `mac-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}
function formatConvDate(dateStr: string): string {
    try {
        return new Date(dateStr).toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric'
        });
    }
    catch {
        return '';
    }
}
function makeMsgTimestamp(): string {
    return new Date().toLocaleTimeString(undefined, {
        hour: '2-digit',
        minute: '2-digit'
    });
}
function formatMsgTimestamp(dateStr: string): string {
    try {
        return new Date(dateStr).toLocaleTimeString(undefined, {
            hour: '2-digit',
            minute: '2-digit'
        });
    }
    catch {
        return makeMsgTimestamp();
    }
}
function makeWelcomeMsg(): ChatMessageType {
    return {
        id: 'mac-welcome',
        role: 'assistant',
        content: "Hi! I'm your MAC Workflow Assistant. How can I help you build or manage workflows today?",
        timestamp: makeMsgTimestamp()
    };
}
const MacAIChatPage: React.FC = () => {
    const uid = localStorage.getItem('nowCertsUserId') ?? '';
    const [conversations, setConversations] = useState<MacAIConversation[]>([]);
    const [activeConversation, setActiveConversation] = useState<MacAIConversation | null>(null);
    const [messages, setMessages] = useState<ChatMessageType[]>([]);
    const [statusText, setStatusText] = useState<string | null>(null);
    const [streamingContent, setStreamingContent] = useState('');
    const [isStreaming, setIsStreaming] = useState(false);
    const [isLoadingConvs, setIsLoadingConvs] = useState(true);
    const [isLoadingMsgs, setIsLoadingMsgs] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [notSubscribed, setNotSubscribed] = useState(false);
    const abortRef = useRef<AbortController | null>(null);
    const sendPromptRef = useRef<(text: string) => void>(() => { });
    const latestConvIdRef = useRef<string | null>(null);
    const isSendingRef = useRef(false);
    const scrollRef = useAutoScroll(messages, streamingContent);
    useEffect(() => {
        return () => {
            abortRef.current?.abort();
        };
    }, []);
    useEffect(() => {
        const handler = (e: MessageEvent) => {
            if (e.data?.type !== 'mac_sendPrompt')
                return;
            if (e.origin !== 'null' && e.origin !== window.location.origin)
                return;
            const text = String(e.data.text ?? '').trim();
            if (!text)
                return;
            sendPromptRef.current(text);
        };
        window.addEventListener('message', handler);
        return () => window.removeEventListener('message', handler);
    }, []);
    useEffect(() => {
        let cancelled = false;
        setIsLoadingConvs(true);
        getMacAIConversations(uid)
            .then(convs => {
            if (!cancelled)
                setConversations(convs);
        })
            .catch((err: unknown) => {
            if (cancelled)
                return;
            if (err instanceof FusionNowNotSubscribedError)
                setNotSubscribed(true);
            else
                setError('Failed to load conversations. Please try again.');
        })
            .finally(() => {
            if (!cancelled)
                setIsLoadingConvs(false);
        });
        return () => {
            cancelled = true;
        };
    }, [uid]);
    const handleSelectConversation = useCallback(async (conv: MacAIConversation) => {
        if (conv.id === activeConversation?.id)
            return;
        latestConvIdRef.current = conv.id;
        setActiveConversation(conv);
        setMessages([]);
        setError(null);
        setIsLoadingMsgs(true);
        try {
            const history = await getMacAIConversationMessages(conv.id);
            if (latestConvIdRef.current !== conv.id)
                return;
            const mapped: ChatMessageType[] = [];
            history.forEach(h => {
                mapped.push({
                    id: makeId(),
                    role: 'user',
                    content: h.question,
                    timestamp: formatMsgTimestamp(h.created_at)
                });
                mapped.push({
                    id: makeId(),
                    role: 'assistant',
                    content: h.response,
                    timestamp: formatMsgTimestamp(h.created_at)
                });
            });
            setMessages(mapped.length > 0 ? mapped : [makeWelcomeMsg()]);
        }
        catch {
            setError('Failed to load conversation history.');
            setMessages([makeWelcomeMsg()]);
        }
        finally {
            setIsLoadingMsgs(false);
        }
    }, [activeConversation?.id]);
    const handleSendPrompt = useCallback(async (promptText: string) => {
        if (!promptText.trim() || isStreaming || isSendingRef.current)
            return;
        isSendingRef.current = true;
        let conv = activeConversation;
        if (!conv) {
            try {
                conv = await createMacAIConversation(uid);
                setConversations(prev => [conv!, ...prev]);
                setActiveConversation(conv);
                latestConvIdRef.current = conv.id;
                setMessages([makeWelcomeMsg()]);
            }
            catch (err: unknown) {
                if (err instanceof FusionNowNotSubscribedError)
                    setNotSubscribed(true);
                else
                    setError('Could not start a conversation. Please try again.');
                return;
            }
        }
        abortRef.current?.abort();
        abortRef.current = new AbortController();
        setError(null);
        setStatusText(null);
        const userMsg: ChatMessageType = {
            id: makeId(),
            role: 'user',
            content: promptText.trim(),
            timestamp: makeMsgTimestamp()
        };
        setMessages(prev => [...prev, userMsg]);
        const aiMsgId = makeId();
        setMessages(prev => [
            ...prev,
            {
                id: aiMsgId,
                role: 'assistant',
                content: '',
                timestamp: makeMsgTimestamp(),
                isStreaming: true
            }
        ]);
        setIsStreaming(true);
        setStreamingContent('');
        setStatusText('Thinking…');
        requestAnimationFrame(() => {
            const el = scrollRef.current;
            if (el)
                el.scrollTop = el.scrollHeight;
        });
        try {
            const { errorMessage } = await sendMacAIMessage(promptText.trim(), conv.id, uid, chunk => {
                if (chunk.widget) {
                    setMessages(prev => prev.map(m => m.id === aiMsgId
                        ? {
                            ...m,
                            isStreaming: false,
                            metadata: { ...m.metadata, widget: chunk.widget }
                        }
                        : m));
                    setIsStreaming(false);
                    setStatusText(null);
                    setStreamingContent('');
                }
                else if (chunk.statusText != null) {
                    setStatusText(chunk.statusText);
                }
                else if (chunk.content != null) {
                    setStatusText(null);
                    setStreamingContent(chunk.content);
                    setMessages(prev => prev.map(m => m.id === aiMsgId
                        ? {
                            ...m,
                            content: chunk.content!,
                            isStreaming: chunk.isStreaming
                        }
                        : m));
                }
            }, abortRef.current.signal);
            setMessages(prev => prev.map(m => (m.id === aiMsgId ? { ...m, isStreaming: false } : m)));
            if (errorMessage)
                setError(errorMessage);
        }
        catch (err: unknown) {
            setMessages(prev => prev.map(m => (m.id === aiMsgId ? { ...m, isStreaming: false } : m)));
            if ((err as Error).name !== 'AbortError') {
                if (err instanceof FusionNowNotSubscribedError)
                    setNotSubscribed(true);
                else
                    setError('Something went wrong. Please try again.');
            }
        }
        finally {
            isSendingRef.current = false;
            setIsStreaming(false);
            setStatusText(null);
            setStreamingContent('');
        }
    }, [isStreaming, activeConversation, uid, scrollRef]);
    const handleNewChat = useCallback(() => {
        abortRef.current?.abort();
        isSendingRef.current = false;
        latestConvIdRef.current = null;
        setActiveConversation(null);
        setMessages([]);
        setIsStreaming(false);
        setStreamingContent('');
        setStatusText(null);
        setError(null);
    }, []);
    sendPromptRef.current = handleSendPrompt;
    const handleWidgetLoad = useCallback(() => {
        const el = scrollRef.current;
        if (el)
            el.scrollTop = el.scrollHeight;
    }, [scrollRef]);
    if (notSubscribed) {
        return (<div className='amp-chat'>
        <MacSubscribePrompt />
      </div>);
    }
    const showWelcome = !activeConversation;
    return (<div className='amp-chat'>
      {!showWelcome && (<div className='mac-page__header'>
          <span className='mac-page__header-title'>
            {activeConversation?.title || 'MAC Workflows'}
          </span>
          <button className='mac-page__new-chat-btn' onClick={handleNewChat} title='New chat'>
            <FiEdit3 size={14}/>
            <span>New Chat</span>
          </button>
        </div>)}
      <div className='amp-chat__container' ref={scrollRef}>
        <div className='amp-chat__messages'>
          
          {showWelcome && (<motion.div className='amp-welcome' initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1 }}>
              <div className='amp-welcome__icon-wrap'>
                <div className='amp-welcome__icon' style={{
                background: 'linear-gradient(135deg, rgba(162,0,255,0.16), rgba(124,58,237,0.08))',
                color: '#a200ff',
                border: '1px solid rgba(162,0,255,0.22)'
            }}>
                  <FiZap size={28}/>
                </div>
                <div className='amp-welcome__icon-glow' style={{ background: '#a200ff' }}/>
              </div>
              <h2 className='amp-welcome__title'>MAC Workflows</h2>
              <p className='amp-welcome__desc'>
                Build, explore, and manage your insurance workflows with AI.
              </p>
              <div className='amp-welcome__hints'>
                <span className='amp-welcome__hint'>
                  Specialized AI for workflow automation
                </span>
                <span className='amp-welcome__hint'>
                  Build and manage workflows
                </span>
                <span className='amp-welcome__hint'>
                  All conversations are saved
                </span>
              </div>
            </motion.div>)}

          
          {showWelcome && (<div className='channel-prompts'>
              {MAC_PROMPTS.map((item, i) => (<motion.button key={item.id} className='channel-prompts__card glass-surface' onClick={() => handleSendPrompt(item.prompt)} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 + i * 0.06, duration: 0.35 }} whileHover={{ scale: 1.02, y: -2 }} whileTap={{ scale: 0.98 }}>
                  <span className='channel-prompts__icon' style={{ color: item.color }}>
                    {item.icon}
                  </span>
                  <span className='channel-prompts__prompt'>{item.label}</span>
                </motion.button>))}
            </div>)}

          
          {showWelcome && (<motion.div className='channel-history' initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.15 }}>
              <div className='channel-history__header'>
                <FiMessageSquare size={13}/>
                <span>Recent Conversations</span>
              </div>
              {isLoadingConvs ? (<div className='channel-history__loading'>Loading history…</div>) : conversations.length === 0 ? (<div className='channel-history__loading'>
                  No conversations yet
                </div>) : (<div className='channel-history__list'>
                  {conversations.map((conv, i) => (<motion.button key={conv.id} className='channel-history__item' onClick={() => handleSelectConversation(conv)} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }} whileHover={{ x: 4 }}>
                      <FiMessageSquare size={13} className='channel-history__item-icon'/>
                      <span className='channel-history__item-title'>
                        {conv.title || 'Untitled'}
                      </span>
                      <span className='channel-history__item-time'>
                        {conv.created_at ? formatConvDate(conv.created_at) : ''}
                      </span>
                    </motion.button>))}
                </div>)}
            </motion.div>)}

          
          {isLoadingMsgs && (<div className='mac-page__loading'>
              <div className='mac-page__spinner'/>
              <span>Loading history…</span>
            </div>)}

          
          {!isLoadingMsgs &&
            messages.map((msg, idx) => (<ChatMessage key={msg.id} message={msg} chatId={activeConversation?.id} onWidgetLoad={handleWidgetLoad} disableFeedback={true} streamingStatusText={msg.isStreaming ? statusText : undefined} onQuickReply={idx === messages.length - 1 && msg.role === 'assistant' && !isStreaming ? handleSendPrompt : undefined}/>))}

          
          {error && (<div className='mac-page__error'>
              <FiAlertCircle size={14}/>
              <span>{error}</span>
              <button onClick={() => setError(null)}>Dismiss</button>
            </div>)}
        </div>
      </div>

      
      <div className='amp-chat__input-area'>
        <ChatInput onSend={handleSendPrompt} disabled={isStreaming || isLoadingMsgs} selectedAmpId={null} conversationId={activeConversation?.id} placeholder='Message MAC Workflows...' hideAmpDropdown hideAttachButton/>
      </div>
    </div>);
};
export default MacAIChatPage;
